import React, { useEffect, useState, useRef } from 'react';
import { ChevronDown, Settings } from 'lucide-react';
interface Property {
  id: string;
  name: string;
  address: string;
  imageUrl: string;
}
interface PropertySelectorProps {
  selectedProperty?: Property;
  onSelect?: (property: Property) => void;
  isCollapsed?: boolean;
}
export function PropertySelector({
  selectedProperty,
  onSelect,
  isCollapsed = false
}: PropertySelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const properties: Property[] = [{
    id: '1',
    name: 'Villa Sunset',
    address: 'Via Panoramica 24, Amalfi',
    imageUrl: 'https://cdn.magicpatterns.com/uploads/wZEvHZWrftB2ptjVQe92n8/J24_Miro_Room_01.jpg'
  }, {
    id: '2',
    name: 'Appartamento Centro',
    address: 'Via Roma 15, Milano',
    imageUrl: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&q=80&w=200'
  }, {
    id: '3',
    name: 'Casa al Mare',
    address: 'Lungomare 8, Positano',
    imageUrl: 'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?auto=format&fit=crop&q=80&w=200'
  }, {
    id: '4',
    name: 'Loft Moderno',
    address: 'Via Garibaldi 42, Torino',
    imageUrl: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&q=80&w=200'
  }];
  const current = selectedProperty || properties[0];
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);
  const handleSelect = (property: Property) => {
    if (onSelect) {
      onSelect(property);
    }
    setIsOpen(false);
  };
  const handleGoToDetail = () => {
    window.location.href = '/scheda-proprieta';
    setIsOpen(false);
  };
  return <div ref={dropdownRef} className={`
        flex-shrink-0 transition-all duration-300 ease-in-out relative
        ${isCollapsed ? 'px-0 py-3' : 'px-3 py-3 border-b border-white/10'}
      `} data-id="element-518">
      {/* Section label — only when expanded */}
      {!isCollapsed && <p className="text-[11px] font-medium text-white/40 uppercase tracking-widest mb-2 px-0.5" data-id="element-519">
          Seleziona il tuo B&B
        </p>}

      {/* Single button that adapts between collapsed (image only) and expanded (image + text) */}
      <button onClick={() => setIsOpen(!isOpen)} title={current.name} className={`
          flex items-center transition-all duration-300 ease-in-out overflow-hidden
          ${isCollapsed ? 'w-10 h-10 mx-auto rounded-lg border-2 border-white/20 hover:border-white/40' : 'w-full bg-white/10 hover:bg-white/15 border border-white/20 rounded-xl p-2.5 gap-3'}
        `} data-id="element-520">
        {/* Property image — always visible */}
        <div className={`
            rounded-lg overflow-hidden flex-shrink-0 transition-all duration-300 ease-in-out
            ${isCollapsed ? 'w-full h-full' : 'w-9 h-9 border border-white/20'}
          `} data-id="element-521">
          <img src={current.imageUrl} alt={current.name} className="w-full h-full object-cover" data-id="element-522" />
        </div>

        {/* Text + chevron — fade in/out */}
        <div className={`
            flex items-center justify-between flex-1 min-w-0
            transition-all duration-300 ease-in-out
            ${isCollapsed ? 'w-0 opacity-0 overflow-hidden' : 'opacity-100'}
          `} data-id="element-523">
          <p className="text-sm font-semibold text-white truncate" data-id="element-524">
            {current.name}
          </p>
          <ChevronDown className={`w-4 h-4 text-white/50 flex-shrink-0 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-525" />
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && <div className={`
            absolute bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden
            ${isCollapsed ? 'top-0 left-full ml-2 w-56' : 'top-full left-3 right-3 mt-2'}
          `} data-id="element-526">
          <div className="max-h-64 overflow-y-auto py-1" data-id="element-527">
            {properties.map(property => <button key={property.id} onClick={() => handleSelect(property)} className={`w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors text-left ${property.id === current.id ? 'bg-coral/5' : ''}`} data-id="element-528">
                <div className="w-8 h-8 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0" data-id="element-529">
                  <img src={property.imageUrl} alt={property.name} className="w-full h-full object-cover" data-id="element-530" />
                </div>
                <div className="flex-1 overflow-hidden" data-id="element-531">
                  <p className={`text-sm font-medium truncate ${property.id === current.id ? 'text-coral' : 'text-navy'}`} data-id="element-532">
                    {property.name}
                  </p>
                </div>
                {property.id === current.id && <div className="w-2 h-2 rounded-full bg-coral flex-shrink-0" data-id="element-533"></div>}
              </button>)}
          </div>

          <div className="border-t border-gray-100" data-id="element-534"></div>

          <button onClick={handleGoToDetail} className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors text-left" data-id="element-535">
            <div className="w-8 h-8 rounded-lg bg-coral/10 flex items-center justify-center flex-shrink-0" data-id="element-536">
              <Settings className="w-4 h-4 text-coral" data-id="element-537" />
            </div>
            <div className="flex-1" data-id="element-538">
              <p className="text-sm font-medium text-coral" data-id="element-539">
                Vai a Scheda Proprietà
              </p>
              <p className="text-xs text-gray-500" data-id="element-540">
                Gestisci dettagli e impostazioni
              </p>
            </div>
          </button>
        </div>}
    </div>;
}